import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ConfiguratorScene } from '@3d-scene/scene/configurator-scene';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';
import { Box3, Camera, OrthographicCamera, Vector3, WebGLRenderTarget } from 'three';
import { Injectable } from '@angular/core';

type ViewDirection = 'top' | 'bottom' | 'left' | 'right' | 'front' | 'back';

@Injectable({ providedIn: 'root' })
export class ScreenShotService {

  constructor(
    private configuratorScene: ConfiguratorScene,
  ) { }

  public orthogonalScreenshot(bounds: Box3, direction: ViewDirection, resolutionWidth: number, resolutionHeight: number, padding: number) {
    const renderTarget = new WebGLRenderTarget(resolutionWidth, resolutionHeight);
    const camera = this.createCameraFromDirection(direction, bounds, padding);
    const renderer = this.configuratorScene.renderer.webGLRenderer;
    renderer.setRenderTarget(renderTarget);
    renderer.render(this.configuratorScene.scene, camera);
    renderer.setRenderTarget(null);
    this.configuratorScene.renderScene();
    return renderTarget.texture;
  }

  private createCameraFromDirection(dir: ViewDirection, bounds: Box3, padding: number): OrthographicCamera {
    let camera: OrthographicCamera;
    const viewDistance = 50000;
    const near = 10;
    const far = 100000;
    switch (dir) {
      case 'front':
        camera = new OrthographicCamera(bounds.min.x - padding, bounds.max.x + padding, bounds.max.y + padding, bounds.min.y - padding, near, far);
        camera.position.set(bounds.min.x, 0, -viewDistance);
        this.rotateCamera(camera, 180, 0);
        return camera;
      case 'back':
        camera = new OrthographicCamera(bounds.min.x - padding, bounds.max.x + padding, bounds.max.y + padding, bounds.min.y - padding, near, far);
        camera.position.set(bounds.max.x, 0, viewDistance);
        this.rotateCamera(camera, 360, 0);
        break;
      case 'top':
        camera = new OrthographicCamera(bounds.min.x - padding, bounds.max.x + padding, bounds.max.z + padding, bounds.min.z - padding, near, far);
        camera.position.set(bounds.min.x, viewDistance, 0);
        this.rotateCamera(camera, 180, 90);
        break;
      case 'bottom':
        camera = new OrthographicCamera(bounds.min.x - padding, bounds.max.x + padding, bounds.max.z + padding, bounds.min.z - padding, near, far);
        camera.position.set(bounds.min.x, -viewDistance, 0);
        this.rotateCamera(camera, 180, 270);
        break;
      case 'left':
        camera = new OrthographicCamera(bounds.min.z - padding, bounds.max.z + padding, bounds.max.y + padding, bounds.min.y - padding, near, far);
        camera.position.set(viewDistance, 0, 0);
        this.rotateCamera(camera, 90, 0);
        break;
      case 'right':
        camera = new OrthographicCamera(bounds.min.z - padding, bounds.max.z + padding, bounds.max.y + padding, bounds.min.y - padding, near, far);
        camera.position.set(-viewDistance, 0, 0);
        this.rotateCamera(camera, 270, 0);
        break;
      default:
        assertExhaustiveSwitch(dir);
    }
    return camera;
  }

  private rotateCamera(camera: Camera, panAngleDeg: number, tiltAngleDeg: number): void {
    camera.rotateOnAxis(new Vector3(1, 0, 0), MathUtils.toRadians(-tiltAngleDeg));
    camera.rotateOnWorldAxis(new Vector3(0, 1, 0), MathUtils.toRadians(panAngleDeg));
  }
}
